import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useList, useCreate, useOne, useUpdate } from '@refinedev/core';
import { WizardLayout } from '@/components/wizard';
import { PricingTiersSection } from '@/components/pricing-tiers';
import { ModulesInlineEditor } from '@/resources/modules';
import { CourseFormFields, initFormState } from './components/CourseFormFields';
import { buildTeacherOptions, type TeacherRecord } from './components/select-utils';
import type { Course, CourseFormData } from './types';
import { LanguageSwitcher, type TranslationLanguage, type LanguageCode } from '@/components/translations';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BookOpen, DollarSign, Boxes, Plus } from 'lucide-react';
import { notify } from '@/lib/notify';

// ─── Steps ──────────────────────────────────────────────────────────────────

const STEPS = [
  { key: 'basics', label: 'Основные данные' },
  { key: 'pricing', label: 'Тарифы' },
  { key: 'modules', label: 'Модули' },
  { key: 'review', label: 'Обзор' },
];

const TRANSLATION_FIELDS = [
  { name: 'title', label: 'Название', type: 'input' as const },
];

// ─── Language record from API ───────────────────────────────────────────────

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
}

// ─── ReviewStep ─────────────────────────────────────────────────────────────

function ReviewStep({ course, onStepClick }: {
  course: Course;
  onStepClick: (key: string) => void;
}) {
  const title = course.translations?.[0]?.title ?? course.slug;
  const tiersCount = course.pricingTiers?.length ?? 0;
  const modulesCount = course.modules?.length ?? 0;
  const totalItems = course.modules?.reduce((acc, m) => acc + (m.items?.length ?? 0), 0) ?? 0;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Обзор</h2>
      <p className="text-sm text-muted-foreground">Проверьте данные перед публикацией курса.</p>

      {/* Course basics */}
      <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => onStepClick('basics')}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Основные данные
            </CardTitle>
            <Badge variant="outline">Редактировать</Badge>
          </div>
        </CardHeader>
        <CardContent className="text-sm space-y-1">
          <p><span className="text-muted-foreground">Название:</span> {title}</p>
          <p><span className="text-muted-foreground">Slug:</span> {course.slug}</p>
          <p><span className="text-muted-foreground">Тип:</span> {course.courseType}</p>
          <p><span className="text-muted-foreground">Переводов:</span> {course.translations?.length ?? 0}</p>
        </CardContent>
      </Card>

      {/* Pricing tiers */}
      <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => onStepClick('pricing')}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Тарифы
            </CardTitle>
            <Badge variant="outline">Редактировать</Badge>
          </div>
        </CardHeader>
        <CardContent className="text-sm">
          {tiersCount === 0 ? (
            <p className="text-muted-foreground">Тарифы не добавлены (курс будет бесплатным)</p>
          ) : (
            <p>{tiersCount} тариф(ов)</p>
          )}
        </CardContent>
      </Card>

      {/* Modules */}
      <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => onStepClick('modules')}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Boxes className="h-4 w-4" />
              Модули
            </CardTitle>
            <Badge variant="outline">Редактировать</Badge>
          </div>
        </CardHeader>
        <CardContent className="text-sm">
          {modulesCount === 0 ? (
            <p className="text-muted-foreground">Модули не добавлены</p>
          ) : (
            <p>{modulesCount} модулей, {totalItems} уроков</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── CourseWizard ───────────────────────────────────────────────────────────

export function CourseWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('basics');
  // After basics step, we create the course as a draft and store its ID
  const [createdCourseId, setCreatedCourseId] = useState<string | null>(null);
  const [addModuleOpen, setAddModuleOpen] = useState(false);

  // For triggering CourseFormFields submit
  const submitRef = useRef<(() => void) | null>(null);

  // Languages
  const { result: langResult, query: langQ } = useList<LanguageRecord>({ resource: 'languages' });
  const languages: TranslationLanguage[] = (langResult?.data ?? []).map((l) => ({
    code: l.code, name: l.name, nativeName: l.nativeName, flag: l.flag ?? l.flagEmoji ?? '',
  }));

  // Teachers
  const { result: instrResult } = useList<TeacherRecord>({
    resource: 'teachers',
    pagination: { pageSize: 100 },
    filters: [{ field: 'isActive', operator: 'eq', value: true }],
  });
  const instrOpts = buildTeacherOptions(instrResult?.data ?? []);

  // Fetch created course data (for modules tab and review)
  const { result: courseData, query: courseQuery } = useOne<Course>({
    resource: 'courses',
    id: createdCourseId ?? '',
    queryOptions: { enabled: createdCourseId !== null },
  });
  const course = courseData ?? null;

  // Create course mutation
  const createMutation = useCreate<Course>();
  const isCreating = createMutation.mutation.isPending;

  // Update course mutation (for publishing)
  const updateMutation = useUpdate<Course>();
  const isPublishing = updateMutation.mutation.isPending;

  const isSubmitting = isCreating || isPublishing;

  const stepIndex = STEPS.findIndex((s) => s.key === currentStep);
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === STEPS.length - 1;

  const canProceed = (() => {
    if (currentStep === 'basics') { return true; } // validation happens in form submit
    if (currentStep === 'review') { return createdCourseId !== null; }
    return true; // pricing and modules are optional
  })();

  const handleStepClick = (key: string) => {
    const targetIndex = STEPS.findIndex((s) => s.key === key);
    // Allow navigating to any visited step (any step up to current + pricing/modules after basics)
    if (targetIndex <= stepIndex || (createdCourseId !== null && targetIndex <= STEPS.length - 1)) {
      setCurrentStep(key);
    }
  };

  const handleNextStep = () => {
    if (currentStep === 'basics') {
      submitRef.current?.();
      return; // onSubmit callback will advance the step
    }
    const nextIndex = stepIndex + 1;
    if (nextIndex < STEPS.length) {
      // Refetch course data when moving to review
      if (STEPS[nextIndex].key === 'review') {
        courseQuery.refetch();
      }
      setCurrentStep(STEPS[nextIndex].key);
    }
  };

  const handlePrevStep = () => {
    const prevIndex = stepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex].key);
    }
  };

  const handleCourseDataSubmit = useCallback(async (data: Partial<CourseFormData>) => {
    if (createdCourseId) {
      // Course already created — update it and advance
      updateMutation.mutate({
        resource: 'courses',
        id: createdCourseId,
        values: data,
      }, {
        onSuccess: () => {
          courseQuery.refetch();
          setCurrentStep('pricing');
        },
      });
      return;
    }

    // First time — create course as draft
    createMutation.mutate({
      resource: 'courses',
      values: { ...data, isPublished: false },
    }, {
      onSuccess: (response) => {
        const created = response?.data;
        if (created?.id) {
          setCreatedCourseId(String(created.id));
          setCurrentStep('pricing');
        }
      },
      onError: (error) => {
        notify.error(`Ошибка создания курса: ${error.message}`);
      },
    });
  }, [createdCourseId, createMutation, updateMutation, courseQuery]);

  const handleComplete = () => {
    if (!createdCourseId) { return; }

    // Navigate to the course show page
    navigate(`/courses/show/${createdCourseId}`);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'basics':
        return (
          <CourseFormFields
            // updatedAt в ключе: после создания/refetch курс приходит из
            // useOne асинхронно. useReducer фиксирует initial только при
            // монтировании — без ремоунта reducer залипнет на устаревшем
            // снимке и buildUpdateFormData отправит дефолт isPublished:false.
            key={`wizard-create-${createdCourseId ?? 'new'}-${course?.updatedAt ?? 'init'}`}
            isEdit={createdCourseId !== null}
            loading={langQ.isLoading}
            initial={course ? initFormState(course) : initFormState()}
            instrOpts={instrOpts}
            languages={languages}
            courseId={createdCourseId ?? undefined}
            renderHeader={(state, setField) => (
              <div className="flex items-center justify-end gap-3">
                {languages.length > 0 && (
                  <LanguageSwitcher
                    languages={languages}
                    activeLanguage={state.lang}
                    onLanguageChange={(c) => setField('lang', c as LanguageCode)}
                    fields={TRANSLATION_FIELDS}
                    value={state.translations}
                    variant="compact"
                  />
                )}
              </div>
            )}
            renderFooter={(submit) => {
              submitRef.current = submit;
              return null;
            }}
            onSubmit={handleCourseDataSubmit}
          />
        );

      case 'pricing':
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Тарифы</h2>
              <p className="text-sm text-muted-foreground">
                Добавьте тарифы для курса. Этот шаг необязателен — тарифы можно добавить позже.
              </p>
            </div>
            {createdCourseId && (
              <PricingTiersSection
                productKey={{ courseId: Number(createdCourseId) }}
                languages={languages}
              />
            )}
          </div>
        );

      case 'modules':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Модули и уроки</h2>
                <p className="text-sm text-muted-foreground">
                  Добавьте структуру курса. Этот шаг необязателен — модули можно добавить позже.
                </p>
              </div>
              <Button className="shrink-0" onClick={() => setAddModuleOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Добавить модуль
              </Button>
            </div>
            {createdCourseId && course && (
              <ModulesInlineEditor
                courseId={createdCourseId}
                modules={course.modules ?? []}
                onRefresh={() => { courseQuery.refetch(); }}
                addModuleOpen={addModuleOpen}
                onAddModuleOpenChange={setAddModuleOpen}
              />
            )}
          </div>
        );

      case 'review':
        return course ? (
          <ReviewStep
            course={course}
            onStepClick={setCurrentStep}
          />
        ) : null;

      default:
        return null;
    }
  };

  return (
    <WizardLayout
      title="Создать курс"
      steps={STEPS}
      currentStep={currentStep}
      onStepClick={handleStepClick}
      onBack={() => navigate('/courses')}
      onPrevStep={handlePrevStep}
      onNextStep={handleNextStep}
      onComplete={handleComplete}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      canProceed={canProceed}
      isSubmitting={isSubmitting}
    >
      {renderStep()}
    </WizardLayout>
  );
}
